import dayjs from "dayjs"
import {
  BLACKOUT_SCOPES,
  DATE_VALUE_FORMAT,
  MAX_REPEAT_INTERVAL,
  REPEAT_FREQUENCIES,
  WEEKDAY_KEYS,
  formatEntries,
  formatEntry,
  formatTimeRange,
  includesId,
  isAllDayEntry,
  isSameId,
  toRecurrence
} from "helpers/blackouts"
import { FILTER_SUMMARY_MAX_NAMES } from "./constants"

// Entry date/time formatting is shared with the Calendar surfaces — see `helpers/blackouts`.
export { isAllDayEntry, isRangeEntry, formatTimeRange, formatEntry, formatEntries, formatOccurrenceDate } from "helpers/blackouts"

/**
 * The date line for a row: entries, plus the time range when a single entry is timed.
 * A multi-entry blackout keeps just the dates — per-entry times would not fit the row.
 *
 * A repeat dates its first real blackout day, which is not always the day the guide picked — see
 * `getRepeatStartShift`. The edit form keeps the picked day: it is what the guide entered.
 */
export const formatRowDateLabel = (blackout) => {
  const shift = getRepeatStartShift(blackout)
  const entries = getSavedEntries(blackout).map((entry) =>
    shift ? { ...entry, start_date: shift.firstDate, end_date: shift.firstDate } : entry
  )
  const dates = formatEntries(entries)
  if (entries.length !== 1 || isAllDayEntry(entries[0])) return dates
  return `${dates} · ${formatTimeRange(entries[0])}`
}

/**
 * Row identity for an unsaved "Applies on" entry.
 *
 * Saved entries keep the server's `entries[].id`. This only fills in rows the guide has just
 * added, and rows the API returns without an id — `AppliesOnRows` keys, edits and deletes by
 * id, so two rows sharing one would be edited and removed together.
 */
export const createEntryId = () => `entry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

/**
 * A saved record's entries, each with a usable row id.
 *
 * The server's `entries[].id` is preferred — but only when a record's ids are actually distinct.
 * The API has shipped `"e-1"` on every entry, and now returns the record's own id on the entry,
 * so uniqueness is not something to assume. `AppliesOnRows` keys, edits and deletes rows by this
 * id, so a single collision would edit or remove several rows at once. Any duplicate or missing
 * id falls the whole record back to client ids, which are ignored on write (D6) and cost nothing.
 */
export const withRowIds = (entries = []) => {
  const ids = entries.map((entry) => entry.id)
  const areIdsUsable = ids.every(Boolean) && new Set(ids.map(String)).size === ids.length
  return entries.map((entry) => ({ ...entry, id: areIdsUsable ? entry.id : createEntryId() }))
}

/** A fresh, empty "Applies on" row. */
export const createEmptyEntry = () => ({
  id: createEntryId(),
  start_date: null,
  end_date: null,
  start_time: null,
  end_time: null
})

export const ENTRY_ERRORS = {
  MISSING_DATE: "missing_date",
  INCOMPLETE_TIME: "incomplete_time",
  TIME_ORDER: "time_order",
  DUPLICATE: "duplicate",
  OVERLAP: "overlap"
}

/**
 * What is wrong with one "Applies on" row, or null.
 *
 * A dateless row is only an error once the guide has added a second one: the modal opens with a
 * single empty row, and flagging that would put an error on an untouched form. With more than one
 * row it matters — `buildBlackoutPayload` drops undated rows, so without this the guide fills two
 * of three rows, saves, and the third is gone with nothing said.
 *
 * Times are compared as "HH:mm" strings, which orders correctly. An end at or before the start is
 * rejected rather than read as crossing midnight — an entry's times apply *within* each of its
 * days, so there is no shape here that can express an overnight window.
 */
export const getEntryError = (entry, { isOnlyEntry = false } = {}) => {
  if (!entry?.start_date) return isOnlyEntry ? null : ENTRY_ERRORS.MISSING_DATE

  const hasStartTime = Boolean(entry.start_time)
  const hasEndTime = Boolean(entry.end_time)
  if (hasStartTime !== hasEndTime) return ENTRY_ERRORS.INCOMPLETE_TIME
  if (hasStartTime && entry.end_time <= entry.start_time) return ENTRY_ERRORS.TIME_ORDER

  return null
}

/**
 * A row's dates as a comparable range. `YYYY-MM-DD` strings order correctly as strings, and a
 * single-day row is a range whose ends meet.
 */
const toDateRange = (entry) => ({ start: entry.start_date, end: entry.end_date || entry.start_date })

const isSameRange = (first, second) => first.start === second.start && first.end === second.end

const isOverlappingRange = (first, second) => first.start <= second.end && second.start <= first.end

/**
 * Row errors keyed by entry id; an empty object means the whole list is valid.
 *
 * Two rows may not cover the same day. Times are not part of the comparison: a day is either
 * blacked out or it is not, so a second row reaching into days the first already closes says
 * nothing new — whatever window is wanted belongs on the one row. An exact repeat and a partial
 * overlap are told apart only so the message can name what is wrong; both block the form.
 *
 * Only the later row is marked, so the error names the row to fix, and only rows that are
 * themselves valid become a baseline for the ones below. An undated row is never compared — it is
 * either the untouched first row or already carrying `MISSING_DATE`.
 */
export const getEntryErrors = (entries = []) => {
  const acceptedRanges = []

  return entries.reduce((errors, entry) => {
    const error = getEntryError(entry, { isOnlyEntry: entries.length === 1 })
    if (error) {
      errors[entry.id] = error
      return errors
    }
    if (!entry.start_date) return errors

    const range = toDateRange(entry)
    const clash = acceptedRanges.find((accepted) => isOverlappingRange(accepted, range))
    if (clash) errors[entry.id] = isSameRange(clash, range) ? ENTRY_ERRORS.DUPLICATE : ENTRY_ERRORS.OVERLAP
    else acceptedRanges.push(range)
    return errors
  }, {})
}

/**
 * Flattens the nested target options into the leaf ids the API stores.
 * Selecting a parent (e.g. "Kayak · 5 assets") means selecting all of its assets.
 */
export const collectSelectableIds = (options = []) =>
  options.reduce((ids, option) => {
    if (option.children?.length) return [...ids, ...option.children.map((child) => child.id)]
    return [...ids, option.id]
  }, [])

/** Looks up an option or child option by id so chips can render its label. */
export const findOptionById = (options = [], id) => {
  for (const option of options) {
    if (isSameId(option.id, id)) return option
    const child = option.children?.find((candidate) => isSameId(candidate.id, id))
    if (child) return child
  }
  return null
}

/** "Leslie Alexander (Capitan)" — label plus optional suffix. */
export const formatOptionLabel = (option) => (option?.suffix ? `${option.label} ${option.suffix}` : option?.label || "")

/** True when every child of a parent option is selected. */
export const areAllChildrenSelected = (option, selectedIds = []) =>
  Boolean(option.children?.length) && option.children.every((child) => includesId(selectedIds, child.id))

/** True when some but not all children are selected — the parent renders indeterminate. */
export const areSomeChildrenSelected = (option, selectedIds = []) =>
  Boolean(option.children?.length) &&
  option.children.some((child) => includesId(selectedIds, child.id)) &&
  !areAllChildrenSelected(option, selectedIds)

/**
 * Chips collapse to the parent when all of its children are selected, and expand to
 * individual assets otherwise — the two states visible in the equipment design.
 */
export const buildSelectedChips = (options = [], selectedIds = []) =>
  options.reduce((chips, option) => {
    if (!option.children?.length) {
      return includesId(selectedIds, option.id) ? [...chips, { id: option.id, label: formatOptionLabel(option) }] : chips
    }
    if (areAllChildrenSelected(option, selectedIds)) {
      return [...chips, { id: option.id, label: formatOptionLabel(option), childIds: option.children.map((child) => child.id) }]
    }
    const selectedChildren = option.children.filter((child) => includesId(selectedIds, child.id))
    return [...chips, ...selectedChildren.map((child) => ({ id: child.id, label: formatOptionLabel(child) }))]
  }, [])

/**
 * A repeating blackout's one row: the earliest dated row, as a single day, keeping its time window.
 * A repeat starts from one day and "Ends" says when it stops, so a range or a second row would be
 * a competing span — see `AppliesOnRows`.
 */
export const toSingleDateEntries = (entries = []) => {
  const [first] = entries.filter((entry) => entry.start_date).sort((one, other) => one.start_date.localeCompare(other.start_date))
  if (!first) return entries.length ? [entries[0]] : [createEmptyEntry()]
  return [{ ...first, end_date: first.start_date }]
}

/**
 * A saved record's entries as the row and the form read them. A repeating record is one day plus
 * its rule, but the API can return that entry with a later `end_date` (the rule's `ends_on`) — read
 * as-is, every repeat would show as a range running to the end date. Legacy `recurring_yearly`
 * records have no `recurrence` and keep their real ranges.
 */
export const getSavedEntries = (blackout) => {
  const entries = blackout?.entries || []
  return blackout?.recurrence?.frequency && entries.length ? toSingleDateEntries(entries) : entries
}

/**
 * A repeat whose picked start is not one of its own days — "every 2 weeks on Mon, Fri" started on
 * a Wednesday — first closes a later day; the picked day itself stays open. Returns both days so
 * the row can date the real start and say why it differs, or null when they agree.
 *
 * Only weekly rules can differ: a daily, monthly or yearly rule's first day is the picked one.
 *
 * @returns {?{pickedDate: string, firstDate: string}} "YYYY-MM-DD" each
 */
export const getRepeatStartShift = (blackout) => {
  if (!blackout?.recurrence?.frequency) return null
  const entries = getSavedEntries(blackout)
  const pickedDate = getFirstEntryDate(entries)
  if (!pickedDate) return null
  const firstDate = getRecurrencePreview(toRecurrence(blackout), entries, { nextCount: 1 })?.next[0]?.start_date
  return firstDate && firstDate !== pickedDate ? { pickedDate, firstDate } : null
}

/** The blackout's first date — what a repeat is measured from. */
export const getFirstEntryDate = (entries = []) =>
  entries
    .map((entry) => entry.start_date)
    .filter(Boolean)
    .sort()[0] || null

/**
 * What ticking "Repeat" starts from: every day, never ending — the first option the guide sees.
 * A weekly rule preselects the first date's weekday, so switching to Weekly is already valid.
 */
export const createDefaultRecurrence = (entries = []) => {
  const firstDate = getFirstEntryDate(entries)
  return {
    frequency: REPEAT_FREQUENCIES.DAILY,
    interval: 1,
    weekdays: firstDate ? [WEEKDAY_KEYS[dayjs(firstDate).day()]] : [],
    ends_on: null
  }
}

export const RECURRENCE_ERRORS = {
  INTERVAL: "interval",
  NO_WEEKDAYS: "no_weekdays",
  ENDS_BEFORE_START: "ends_before_start"
}

/**
 * Why a repeat rule cannot be saved, as one of `RECURRENCE_ERRORS`, or null when it is fine.
 *
 * @param {?{frequency: string, interval: number, weekdays: string[], ends_on: ?string}} recurrence
 * @param {Object[]} entries the "Applies on" rows, for the date the repeat starts from
 */
export const getRecurrenceError = (recurrence, entries = []) => {
  if (!recurrence) return null
  const { interval, frequency, weekdays = [], ends_on: endsOn } = recurrence
  // Cleared, fractional or out of range — the number box allows all three while typing.
  if (!Number.isInteger(interval) || interval < 1 || interval > MAX_REPEAT_INTERVAL) return RECURRENCE_ERRORS.INTERVAL
  if (frequency === REPEAT_FREQUENCIES.WEEKLY && !weekdays.length) return RECURRENCE_ERRORS.NO_WEEKDAYS
  // An end on the first date itself leaves nothing to repeat, so it must come strictly after.
  // "YYYY-MM-DD" strings compare in date order.
  const firstDate = getFirstEntryDate(entries)
  if (endsOn && firstDate && endsOn <= firstDate) return RECURRENCE_ERRORS.ENDS_BEFORE_START
  return null
}

/** Stops a rule that never ends — or a 99-year one — from walking forever while counting. */
const MAX_PREVIEW_OCCURRENCES = 500
const isTotalCapped = (occurrences) => occurrences.length >= MAX_PREVIEW_OCCURRENCES

/**
 * Hard stop on how many steps one expansion may walk. A listing asks for the next dates from today,
 * so a daily rule that began years ago walks its past days first; this keeps a bad rule from hanging.
 */
const MAX_EXPANSION_STEPS = 10000

/**
 * The days a repeat starts an occurrence on, from `startDate`, in order — the rules the backend
 * expands by (.claude/docs/blackout-recurrence-backend.md):
 *  - daily / weekly step by `interval` days / weeks; weekly weeks are counted Sunday-first from
 *    the start date's own week, and only the picked weekdays inside each one count;
 *  - monthly / yearly keep the start's day (and month), and **skip** a month or year without it
 *    rather than moving to its last day — a blackout on the 31st does not happen in April.
 *
 * @param {{limit: number, fromDate?: ?string}} options `fromDate` ("YYYY-MM-DD") leaves out the
 *   days before it, without counting them toward `limit`
 * @returns {dayjs.Dayjs[]} at most `limit` dates, none after `ends_on`
 */
const getOccurrenceStarts = (startDate, recurrence, { limit, fromDate = null }) => {
  const start = dayjs(startDate)
  const from = fromDate && dayjs(fromDate).isAfter(start, "day") ? dayjs(fromDate) : start
  const endsOn = recurrence.ends_on ? dayjs(recurrence.ends_on) : null
  const { frequency, interval } = recurrence
  const starts = []
  const isPastEnd = (date) => endsOn && date.isAfter(endsOn, "day")

  if (frequency === REPEAT_FREQUENCIES.WEEKLY) {
    const dayIndexes = WEEKDAY_KEYS.map((day, index) => (recurrence.weekdays.includes(day) ? index : null)).filter(
      (index) => index !== null
    )
    if (!dayIndexes.length) return starts
    const firstWeek = start.day(0)
    for (let week = 0; starts.length < limit && week < MAX_EXPANSION_STEPS; week += interval) {
      for (const dayIndex of dayIndexes) {
        const date = firstWeek.add(week, "week").day(dayIndex)
        if (isPastEnd(date)) return starts
        if (!date.isBefore(from, "day") && starts.length < limit) starts.push(date)
      }
    }
    return starts
  }

  const unit = { [REPEAT_FREQUENCIES.DAILY]: "day", [REPEAT_FREQUENCIES.MONTHLY]: "month", [REPEAT_FREQUENCIES.YEARLY]: "year" }[frequency]
  if (!unit) return starts
  // dayjs clamps Jan 31 + 1 month to Feb 28; a date that no longer matches the start was clamped,
  // so that month (or Feb 29's off-year) is skipped.
  for (let iteration = 0; starts.length < limit && iteration < MAX_EXPANSION_STEPS; iteration += 1) {
    const date = start.add(iteration * interval, unit)
    if (isPastEnd(date)) break
    const isClamped = unit !== "day" && (date.date() !== start.date() || (unit === "year" && date.month() !== start.month()))
    if (!isClamped && !date.isBefore(from, "day")) starts.push(date)
  }
  return starts
}

/** Every row's occurrences, merged in date order, with a day two rows share counted once. */
const expandEntries = (datedEntries, recurrence, options) =>
  datedEntries
    .flatMap((entry) => {
      const lengthInDays = dayjs(entry.end_date || entry.start_date).diff(dayjs(entry.start_date), "day")
      return getOccurrenceStarts(entry.start_date, recurrence, options).map((date) => ({
        start_date: date.format(DATE_VALUE_FORMAT),
        end_date: date.add(lengthInDays, "day").format(DATE_VALUE_FORMAT)
      }))
    })
    .sort((first, second) => first.start_date.localeCompare(second.start_date))
    // Two rows can land on the same day — a Friday row and a Saturday row both repeating "weekly on
    // Friday" — and that day is one blackout date, not two.
    .filter(
      (occurrence, index, list) =>
        index === 0 || occurrence.start_date !== list[index - 1].start_date || occurrence.end_date !== list[index - 1].end_date
    )

/**
 * The dates a repeating blackout closes: the next few, and how many in all when the rule has an
 * end. Used by the form's "What will happen" box (next from the first date) and by the Settings
 * list (next from today).
 *
 * A preview only — the server owns the real expansion, and the calendar always renders from it.
 *
 * @param {Object} [options]
 * @param {number} [options.nextCount] how many upcoming dates to return
 * @param {?string} [options.fromDate] "YYYY-MM-DD" — `next` starts here instead of the first date
 * @returns {?{next: Object[], last: ?Object, total: ?number, isTotalCapped: boolean}} occurrences are
 *   `{start_date, end_date}`; null when there is nothing to preview; `total`/`last` null for a rule
 *   that never ends (and `last` also when the count hit the cap)
 */
export const getRecurrencePreview = (recurrence, entries = [], { nextCount = 5, fromDate = null } = {}) => {
  if (!recurrence || getRecurrenceError(recurrence, entries)) return null
  const datedEntries = entries.filter((entry) => entry.start_date)
  if (!datedEntries.length) return null

  const all = recurrence.ends_on ? expandEntries(datedEntries, recurrence, { limit: MAX_PREVIEW_OCCURRENCES }) : null
  const isCapped = Boolean(all) && isTotalCapped(all)
  return {
    next: expandEntries(datedEntries, recurrence, { limit: nextCount, fromDate }).slice(0, nextCount),
    last: all && !isCapped ? all[all.length - 1] || null : null,
    total: all ? all.length : null,
    isTotalCapped: isCapped
  }
}

/**
 * The rule as the API takes it. Weekdays picked under Weekly survive a switch to another
 * frequency in the form, so they are dropped here rather than sent with a rule that ignores them.
 */
export const toRecurrencePayload = (recurrence) =>
  recurrence
    ? {
        frequency: recurrence.frequency,
        interval: recurrence.interval,
        weekdays: recurrence.frequency === REPEAT_FREQUENCIES.WEEKLY ? recurrence.weekdays : [],
        ends_on: recurrence.ends_on || null
      }
    : null

/**
 * "Applies on" rows as the API takes them, for both the save and the impact preview.
 *
 * A one-off blackout's rows are date spans, so each carries an `end_date` (the start date again
 * for a single day). A repeating blackout has one row, and that row is a single day — the repeat's
 * `ends_on` is its only "until" — so it is sent with no `end_date` at all. A record saved with a
 * range under the old yearly checkbox is narrowed to its first day here too, rather than sent as
 * a competing span. See .claude/docs/blackout-recurrence-backend.md §2.
 */
export const toEntriesPayload = (entries, recurrence) =>
  (recurrence ? toSingleDateEntries(entries) : entries)
    .filter((entry) => entry.start_date)
    .map((entry) => ({
      start_date: entry.start_date,
      ...(recurrence ? {} : { end_date: entry.end_date || entry.start_date }),
      start_time: entry.start_time || null,
      end_time: entry.end_time || null
    }))

/**
 * Strips UI-only fields before sending a blackout to the API.
 *
 * A business blackout applies to everything and has no picker, so `target_ids` is forced empty
 * rather than passed through — the API has been seen returning a non-empty `target_ids` on a
 * business record, and editing must not echo that back as if the guide had chosen it.
 */
export const buildBlackoutPayload = ({ scope, reason, recurrence, entries, selectedIds }) => ({
  scope,
  reason: reason?.trim() || "",
  // Kept alongside `recurrence` so a backend that has not shipped the new field still saves the
  // one rule it understands — see .claude/docs/blackout-recurrence-backend.md.
  recurring_yearly: recurrence?.frequency === REPEAT_FREQUENCIES.YEARLY && recurrence.interval === 1 && !recurrence.ends_on,
  recurrence: toRecurrencePayload(recurrence),
  entries: toEntriesPayload(entries, recurrence),
  target_ids: scope === BLACKOUT_SCOPES.BUSINESS ? [] : selectedIds || []
})

/**
 * The Date Range pill's label, e.g. "Jun 4 - Jul 2, 2026".
 *
 * Reuses `formatEntry` so the pill collapses a shared month or year exactly as a blackout's own
 * date line does; the dayjs pair is normalised to date strings first so a single-day range does
 * not read as "Jun 4 - 4, 2026".
 */
export const formatFilterRangeLabel = (range) => {
  if (!range?.[0] || !range?.[1]) return ""
  return formatEntry({ start_date: range[0].format(DATE_VALUE_FORMAT), end_date: range[1].format(DATE_VALUE_FORMAT) }, true)
}

/**
 * Target options as the flat leaves a filter checkbox list needs.
 *
 * `collectSelectableIds` does the same walk for ids alone, which is all the form's picker needs;
 * the filters have to render the text too. `suffix` stays separate from `label` rather than being
 * joined by `formatOptionLabel`, because the design greys it — "Dave Brown (Captain)".
 */
export const flattenTargetOptions = (options = []) => {
  const toLeaf = (option) => ({ id: option.id, label: option.label || "", suffix: option.suffix || "" })
  return options.flatMap((option) => (option.children?.length ? option.children.map(toLeaf) : [toLeaf(option)]))
}

/**
 * Filter state -> the query the list endpoint is asked for.
 *
 * Empty filters are omitted rather than sent as empty arrays, so an unfiltered page issues the
 * same bare `GET /guide/blackouts` it always has. See `docs/blackouts-api.md` §2.1.
 */
export const buildBlackoutListParams = ({ dateRange, resourceIds, listingIds } = {}) => {
  const params = {}
  if (dateRange?.[0] && dateRange?.[1]) {
    params.start_date = dateRange[0].format(DATE_VALUE_FORMAT)
    params.end_date = dateRange[1].format(DATE_VALUE_FORMAT)
  }
  if (resourceIds?.length) params.resource_ids = resourceIds
  if (listingIds?.length) params.listing_ids = listingIds
  return params
}


/**
 * What a collapsed filter section says it is filtering by.
 *
 * A short selection is spelled out, because the names are what the guide recognises; past
 * `FILTER_SUMMARY_MAX_NAMES` the string would be longer than the row, so it falls back to a count
 * per group ("5 crew · 4 equipment") — which is also why groups are passed rather than options.
 */
export const formatTargetsSummary = (selectedIds = [], groups = [], { countLabel } = {}) => {
  if (!selectedIds.length) return ""

  const selectedByGroup = groups.map((group) => ({
    label: group.label,
    options: group.options.filter((option) => includesId(selectedIds, option.id))
  }))
  const selected = selectedByGroup.flatMap((group) => group.options)

  // An id with no matching option (a stale filter, or options still loading) still counts.
  if (!selected.length) return countLabel ? countLabel(selectedIds.length) : String(selectedIds.length)

  if (selected.length <= FILTER_SUMMARY_MAX_NAMES) {
    return selected.map((option) => `${option.label}${option.suffix ? ` ${option.suffix}` : ""}`).join(", ")
  }

  const withCounts = selectedByGroup.filter((group) => group.options.length > 0)
  if (withCounts.length > 1 || withCounts[0]?.label) {
    return withCounts.map((group) => `${group.options.length} ${group.label.toLowerCase()}`).join(" · ")
  }
  return countLabel ? countLabel(selected.length) : String(selected.length)
}
